import { h } from '@stencil/core';
import { CircularProductTypes, SprykerProductCardMain, arrayToMap } from '@pes-ui/utils';
import { getCommerceConnectorSettings, getAllDistributors, getNotificationMessage, getFilteredProductIds, categorizeAndLinkProductsWithRelations, normalizeZipCodePattern, startSearch, getBuyOnlineAvailability, getCircularProductsStockAvailability, } from './services/main';
import { isZipCodeValid, getZipCode, saveZipCodeToStorage } from './services/zip-code';
import { getPrices } from './services/price';
import { updateFavoritesInStorage, getSortedFavoritesList, getFavoritesIds, updateFavoriteStatus, } from './services/favorite';
import { getSiteTypeByURL, getSprykerBaseQueryParams } from '../../globals/utils/helpers';
import { isBrowser } from '../../globals/utils';
export class CommerceConnectorMain {
  constructor() {
    this.isLinkedCircularProductAvaliableInStock = false;
    this.productTabLabelsMap = {};
    this.ccActiveProductType = CircularProductTypes.LINEAR;
    this.ccActiveProductTabLabel = '';
    this.ccStandardProductTabLabel = '';
    this.ccCircularProductTabLabel = '';
    this.hasEnabledLinkedCircularProduct = false;
    this.productsAvailability = {};
    this.productMap = null;
    this.host = undefined;
    this.products = undefined;
    this.productIdToShow = undefined;
    this.additionalDistributors = undefined;
    this.siteType = undefined;
    this.localeCode = undefined;
    this.isPdp = false;
    this.circularEconomy = null;
    this.countryCode = undefined;
    this.settings = undefined;
    this.productsDistributorsInfo = {};
    this.notification = undefined;
    this.isZipCodeValid = true;
    this.zipCode = undefined;
    this.showLoader = undefined;
    this.showValidateMessage = undefined;
    this.showContent = undefined;
    this.isSearchInputPristine = true;
    this.favoriteDistributors = undefined;
    this.isOpen = false;
    this.retailersByProduct = {};
  }
  onItemSelect(event) {
    this.ccActiveProductTabLabel = event.detail;
    const selectedProductTab = this.productTabLabelsMap[this.ccActiveProductTabLabel];
    this.ccActiveProductType = selectedProductTab.type;
    this.productIdToShow = selectedProductTab.commercialReference;
  }
  dialogCloseHandler() {
    const { useZipCode, searchOnOpen } = this.settings.options;
    this.showValidateMessage = false;
    this.isSearchInputPristine = true;
    this.ccDialogClosed.emit();
    if (useZipCode && !searchOnOpen) {
      this.showContent = false;
    }
    if (this.hasEnabledLinkedCircularProduct && this.isLinkedCircularProductAvaliableInStock) {
      this.ccActiveProductType = CircularProductTypes.LINEAR;
      this.ccActiveProductTabLabel = this.ccStandardProductTabLabel;
    }
    this.isOpen = false;
  }
  favoriteRetailIconClickHandler(e) {
    const { options: { countryLanguageCode }, } = this.settings;
    updateFavoritesInStorage(e.detail, countryLanguageCode);
    this.initUpdateFavoritesList();
  }
  buyOnlineSearchClickHandler(event) {
    const { options: { zipCodePattern, countryLanguageCode, feedUrl, circularFeedUrl, useZipCode, searchOnOpen, enableEmptyZipCode, }, labels: { inStock, outOfStock }, } = this.settings;
    const value = event.detail;
    this.isZipCodeValid = (value === '') || isZipCodeValid(zipCodePattern, value);
    this.showValidateMessage = !this.isZipCodeValid;
    if (!this.isZipCodeValid)
      return;
    if (value !== this.zipCode) {
      this.zipCode = value;
      saveZipCodeToStorage(countryLanguageCode, this.zipCode);
    }
    this.productInfo
      && this.initSearch({
        standardProductCommercialReference: this.isccActiveProductTypeStandard
          ? this.productIdToShow : null,
        circularProductCommercialReference: this.isccActiveProductTypeStandard
          ? null : this.productIdToShow,
        zipCode: this.zipCode,
        standardProductFeedUrl: feedUrl,
        circularFeedUrl,
        inStock,
        outOfStock,
        useZipCode,
        searchOnOpen,
        enableEmptyZipCode,
        showContent: true,
        showPrice: this.settings.options.enablePrice,
      });
  }
  buyOnlineSearchInputChangedHandler() {
    this.isSearchInputPristine = false;
  }
  onCircularProductStockAvailabilityEvent(e) {
    this.productsAvailability = Object.assign(this.productsAvailability, e.detail);
    this.ccBtnBuyOnlineAvailability.emit(this.productsAvailability);
  }
  onProductsChange() {
    if (!this.productMap)
      return;
    this.products.forEach((newProduct) => {
      const product = this.productMap.get(newProduct.commercialReference);
      if (!product)
        return;
      Object.assign(product, newProduct);
    });
  }
  async onProductToShowIdChange() {
    var _a, _b, _c;
    if (!this.productIdToShow) {
      return;
    }
    if (this.isOpen) {
      this.updateDistributors();
      return;
    }
    this.ccActiveProductType = this.productInfo.circularType || CircularProductTypes.LINEAR;
    this.hasEnabledLinkedCircularProduct = this.productInfo.circularType === CircularProductTypes.LINEAR
      && this.circularEconomy
      && this.productInfo.circularProductRelationships
      && !!((_a = this.productInfo.circularProductRelationships[0]) === null || _a === void 0 ? void 0 : _a.productId);
    if (!this.hasEnabledLinkedCircularProduct) {
      this.updateDistributors();
      this.isOpen = true;
      return;
    }
    const { productId } = this.productInfo.circularProductRelationships[0];
    this.isLinkedCircularProductAvaliableInStock = !!this.productsAvailability[productId];
    if (!this.isLinkedCircularProductAvaliableInStock) {
      this.updateDistributors();
      this.isOpen = true;
      return;
    }
    const { circularType: productCircularType } = this.productInfo.circularProductRelationships[0];
    this.ccStandardProductTabLabel = this.productInfo.circularNatureLabel;
    this.ccCircularProductTabLabel = (_b = this.circularEconomy) === null || _b === void 0 ? void 0 : _b.circularNatureLabels[productCircularType];
    this.productTabLabelsMap = {
      [this.ccStandardProductTabLabel]: {
        type: CircularProductTypes.LINEAR,
        commercialReference: this.productInfo.commercialReference,
      },
      [this.ccCircularProductTabLabel]: {
        type: productCircularType,
        commercialReference: productId,
      },
    };
    this.ccActiveProductTabLabel = this.ccStandardProductTabLabel;
    const linkedCircularProduct = this.productMap.get(productId);
    const isLinkedCircularProductInfoAvailable = (linkedCircularProduct === null || linkedCircularProduct === void 0 ? void 0 : linkedCircularProduct.description)
      && (linkedCircularProduct === null || linkedCircularProduct === void 0 ? void 0 : linkedCircularProduct.imgUrl)
      && (linkedCircularProduct === null || linkedCircularProduct === void 0 ? void 0 : linkedCircularProduct.imgAlt);
    if (linkedCircularProduct && isLinkedCircularProductInfoAvailable) {
      this.updateDistributors();
      this.isOpen = true;
      return;
    }
    const { countryCode, languageCode } = getSprykerBaseQueryParams(this.host);
    const { result, error } = await SprykerProductCardMain.getProductCardMain({
      countryCode,
      languageCode,
      productIdList: productId,
    });
    if (error) {
      console.error('[PES]: Could not fetch products', error);
      this.updateDistributors();
      this.isOpen = true;
      return;
    }
    if ((_c = result.products) === null || _c === void 0 ? void 0 : _c.length) {
      const { description, pictureUrl, pictureAltText } = result.products[0];
      Object.assign(linkedCircularProduct, { description, imgUrl: pictureUrl, imgAlt: pictureAltText });
    }
    this.updateDistributors();
    this.isOpen = true;
  }
  distributorsChangeHandler() {
    this.notification = getNotificationMessage(this.zipCode, Object.keys(this.productsDistributorsInfo).length, this.settings);
  }
  async componentWillLoad() {
    var _a, _b, _c;
    (_a = this.siteType) !== null && _a !== void 0 ? _a : (this.siteType = getSiteTypeByURL(this.host));
    if (!isBrowser())
      return;
    const response = await getCommerceConnectorSettings(this.host, this.localeCode);
    if (!response)
      return;
    this.ccGetOptionsSucceeded.emit(response.options);
    if (!response.options.enableChannelSight)
      return;
    this.settings = response;
    this.showContent = this.settings.options.useZipCode
      ? this.settings.options.searchOnOpen
      : true;
    this.settings.options.zipCodePattern = normalizeZipCodePattern(this.settings.options.zipCodePattern);
    const { standardProducts, circularProducts, } = categorizeAndLinkProductsWithRelations(this.products);
    this.productMap = arrayToMap(standardProducts.concat(circularProducts), 'commercialReference');
    const { standardProductIds, circularProductIds } = getFilteredProductIds(Array.from(this.productMap.values()));
    const { options: { zipCodePattern, countryLanguageCode, useZipCode, detectZipCode, zipCodeLength, feedUrl, circularFeedUrl, searchOnOpen, enablePrice, priceTemplate, enableEmptyZipCode, }, labels: { inStock, outOfStock, priceFrom: priceFromLabel }, } = this.settings;
    if (useZipCode) {
      this.zipCode = getZipCode({
        countryLanguageCode,
        detect: detectZipCode,
        length: zipCodeLength,
        pattern: zipCodePattern,
      });
    }
    await this.initSearch({
      standardProductCommercialReference: standardProductIds || null,
      circularProductCommercialReference: circularProductIds || null,
      zipCode: this.zipCode,
      standardProductFeedUrl: feedUrl,
      circularFeedUrl,
      inStock,
      outOfStock,
      useZipCode,
      searchOnOpen,
      enableEmptyZipCode,
      showContent: false,
      showPrice: enablePrice,
    });
    const standardProductButtonsBuyOnlineAvailability = getBuyOnlineAvailability(standardProducts, this.productsDistributorsInfo);
    let circularProductStockAvailability = {};
    if (!this.isPdp) {
      circularProductStockAvailability = await getCircularProductsStockAvailability(circularProducts, this.countryCode, (_b = this.circularEconomy) === null || _b === void 0 ? void 0 : _b.apiEndPointUrl, (_c = this.circularEconomy) === null || _c === void 0 ? void 0 : _c.apiKey);
    }
    this.productsAvailability = Object.assign(this.productsAvailability, standardProductButtonsBuyOnlineAvailability, circularProductStockAvailability);
    this.ccBtnBuyOnlineAvailability.emit(this.productsAvailability);
    if (!enablePrice)
      return;
    const prices = getPrices(this.productsDistributorsInfo, {
      template: priceTemplate,
      locale: countryLanguageCode,
    });
    this.ccBroadcastPrices.emit({ priceFromLabel, prices });
  }
  get productInfo() {
    if (!this.productIdToShow) {
      return;
    }
    return this.productMap.get(this.productIdToShow);
  }
  get isccActiveProductTypeStandard() {
    return this.ccActiveProductType === CircularProductTypes.LINEAR;
  }
  async initSearch({ standardProductCommercialReference, circularProductCommercialReference, zipCode, standardProductFeedUrl, circularFeedUrl, inStock, outOfStock, useZipCode, searchOnOpen, enableEmptyZipCode, showContent, showPrice, }) {
    var _a, _b;
    const stockLabels = { inStock, outOfStock };
    this.showLoader = true;
    if (!this.zipCode && !enableEmptyZipCode) {
      this.showContent = false;
    }
    else if (useZipCode && !searchOnOpen) {
      this.showContent = showContent;
    }
    else {
      this.showContent = true;
    }
    try {
      let standardProductData = Promise.resolve({
        productsDataFeed: [],
        distributorProductsInfo: {},
      });
      let circularProductData = Promise.resolve({
        productsDataFeed: [], distributorProductsInfo: {},
      });
      if (standardProductCommercialReference) {
        standardProductData = startSearch({
          feedUrl: standardProductFeedUrl,
          commercialReference: standardProductCommercialReference,
          zipCode,
          stockLabels,
          showPrice,
          siteType: this.siteType,
          isCircularProduct: false,
        });
      }
      if (circularProductCommercialReference) {
        circularProductData = startSearch({
          feedUrl: circularFeedUrl,
          commercialReference: circularProductCommercialReference,
          zipCode,
          stockLabels,
          showPrice,
          siteType: this.siteType,
          isCircularProduct: true,
        });
      }
      const [standardProductDataResult, circularProductDataResult] = await Promise.all([standardProductData, circularProductData]);
      const { productsDataFeed: standardProductsFeed, distributorProductsInfo: standardDistributorInfo, } = standardProductDataResult;
      const { productsDataFeed: circularProductsFeed, distributorProductsInfo: circularDistributorInfo, } = circularProductDataResult;
      this.channelSightResponse.emit(standardProductsFeed.concat(circularProductsFeed));
      const linearRetailers = standardProductsFeed
        .filter(({ RetailerProducts }) => RetailerProducts === null || RetailerProducts === void 0 ? void 0 : RetailerProducts.length)
        .map(({ RetailerProducts }) => RetailerProducts);
      const circularRetailers = circularProductsFeed
        .filter(({ RetailerProducts }) => RetailerProducts === null || RetailerProducts === void 0 ? void 0 : RetailerProducts.length)
        .map(({ RetailerProducts }) => RetailerProducts);
      this.retailersByProduct = {
        linear: standardProductCommercialReference ? linearRetailers : (_a = this.retailersByProduct) === null || _a === void 0 ? void 0 : _a.linear,
        circular: circularProductCommercialReference ? circularRetailers : (_b = this.retailersByProduct) === null || _b === void 0 ? void 0 : _b.circular,
      };
      this.productsDistributorsInfo = Object.assign(Object.assign({}, standardDistributorInfo), circularDistributorInfo);
    }
    catch (error) {
      this.productsDistributorsInfo = {};
      console.error(error);
    }
    finally {
      this.showLoader = false;
    }
  }
  initUpdateFavoritesList() {
    var _a;
    const { options: { countryLanguageCode, priceTemplate }, labels: { notAvailable, inStock, outOfStock }, } = this.settings;
    const distributors = getAllDistributors((_a = this.productInfo) === null || _a === void 0 ? void 0 : _a.commercialReference, this.productsDistributorsInfo, this.additionalDistributors, { priceLocale: countryLanguageCode, priceTemplate });
    this.favoriteDistributors = getSortedFavoritesList(distributors, countryLanguageCode, {
      notAvailable,
      inStock,
      outOfStock,
    });
  }
  async updateDistributors() {
    const { options: { zipCodePattern, feedUrl, circularFeedUrl, useZipCode, searchOnOpen, enableEmptyZipCode, }, labels: { inStock, outOfStock }, } = this.settings;
    this.isZipCodeValid = isZipCodeValid(zipCodePattern, this.zipCode);
    this.showValidateMessage = !this.isZipCodeValid && !this.isSearchInputPristine;
    await this.initSearch({
      standardProductCommercialReference: this.isccActiveProductTypeStandard ? this.productIdToShow
        : null,
      circularProductCommercialReference: this.isccActiveProductTypeStandard ? null
        : this.productIdToShow,
      zipCode: this.zipCode,
      standardProductFeedUrl: feedUrl,
      circularFeedUrl,
      inStock,
      outOfStock,
      useZipCode,
      searchOnOpen,
      enableEmptyZipCode,
      showContent: this.settings.options.useZipCode ? this.settings.options.searchOnOpen : true,
      showPrice: this.settings.options.enablePrice,
    });
    this.initUpdateFavoritesList();
  }
  render() {
    var _a, _b, _c, _d, _e;
    if (!this.settings || !isBrowser()) {
      return null;
    }
    const { priceTemplate, countryLanguageCode: priceLocale } = this.settings.options;
    const favoritesIds = getFavoritesIds(this.favoriteDistributors);
    const distributors = getAllDistributors((_a = this.productInfo) === null || _a === void 0 ? void 0 : _a.commercialReference, this.productsDistributorsInfo, this.additionalDistributors, { priceTemplate, priceLocale });
    updateFavoriteStatus(distributors, favoritesIds);
    return (h("pes-cc-main-view", { isOpen: !!this.productIdToShow && this.isOpen, header: this.settings.labels.modalHeader, product: this.productInfo, isContentVisible: this.showContent, labels: this.settings.labels, distributors: distributors, favorites: this.favoriteDistributors, showLoader: this.showLoader, showSearch: !!this.productIdToShow && this.settings.options.useZipCode, searchHeading: this.settings.labels.searchTitle, searchPlaceholder: this.settings.labels.searchPlaceholder, searchMaxLength: this.settings.options.zipCodeLength, searchButtonLabel: this.settings.labels.searchButton, searchMessage: this.settings.messages.invalidZipCode, searchShowValidateMessage: this.settings.options.useZipCode && this.showValidateMessage, searchValue: this.settings.options.useZipCode ? this.zipCode : undefined, notification: this.notification, ccStandardProductTabLabel: this.ccStandardProductTabLabel, ccCircularProductTabLabel: this.ccCircularProductTabLabel, hasLinearRetailers: !!((_c = (_b = this.retailersByProduct) === null || _b === void 0 ? void 0 : _b.linear) === null || _c === void 0 ? void 0 : _c.length), hasCircularRetailers: !!((_e = (_d = this.retailersByProduct) === null || _d === void 0 ? void 0 : _d.circular) === null || _e === void 0 ? void 0 : _e.length), shouldChipsBeVisible: this.hasEnabledLinkedCircularProduct
        && this.isLinkedCircularProductAvaliableInStock, ccActiveProductTabLabel: this.ccActiveProductTabLabel }));
  }
  static get is() { return "pes-cc-main"; }
  static get encapsulation() { return "shadow"; }
  static get properties() {
    return {
      "host": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "host",
        "reflect": false
      },
      "products": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "Products",
          "resolved": "ProductInfo[]",
          "references": {
            "Products": {
              "location": "import",
              "path": "./types"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "List of products.\n Content of its elements will be used in the header of the modal above the list of distributors."
        }
      },
      "productIdToShow": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Product with this id will be shown in the modal. If no id given then the modal is hidden."
        },
        "attribute": "product-id-to-show",
        "reflect": false
      },
      "additionalDistributors": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "ProductsDistributorsInfo",
          "resolved": "{ [x: string]: Distributor[]; }",
          "references": {
            "ProductsDistributorsInfo": {
              "location": "import",
              "path": "./types"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Dictionary of additional distributors that can be passed from a consumer of the component.\n They will be added to the list of distributors received from the Commerce Connector.\n Should be used to add some distributors such as APC to the modal window."
        }
      },
      "siteType": {
        "type": "string",
        "mutable": true,
        "complexType": {
          "original": "TSiteType",
          "resolved": "\"b2b\" | \"b2c\"",
          "references": {
            "TSiteType": {
              "location": "import",
              "path": "../../globals/types"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Defines the context of the app: B2B or B2C"
        },
        "attribute": "site-type",
        "reflect": false
      },
      "localeCode": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "locale-code",
        "reflect": false
      },
      "isPdp": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "is-pdp",
        "reflect": false,
        "defaultValue": "false"
      },
      "circularEconomy": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "CircularEconomyNew",
          "resolved": "{ apiEndPointUrl: string; apiKey?: string; circularNatureLabels: Record<CircularProductTypes, string>; stockQuantityLabel?: string; newBadge: boolean; newBadgeLabel?: string; }",
          "references": {
            "CircularEconomyNew": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "defaultValue": "null"
      },
      "countryCode": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "country-code",
        "reflect": false
      }
    };
  }
  static get states() {
    return {
      "settings": {},
      "productsDistributorsInfo": {},
      "notification": {},
      "isZipCodeValid": {},
      "zipCode": {},
      "showLoader": {},
      "showValidateMessage": {},
      "showContent": {},
      "isSearchInputPristine": {},
      "favoriteDistributors": {},
      "isOpen": {},
      "retailersByProduct": {}
    };
  }
  static get events() {
    return [{
        "method": "ccDialogClosed",
        "name": "ccDialogClosed",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "complexType": {
          "original": "null",
          "resolved": "null",
          "references": {}
        }
      }, {
        "method": "channelSightResponse",
        "name": "channelSightResponse",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "complexType": {
          "original": "ProductsFeed",
          "resolved": "FeedProduct[]",
          "references": {
            "ProductsFeed": {
              "location": "import",
              "path": "./types"
            }
          }
        }
      }, {
        "method": "ccBroadcastPrices",
        "name": "ccBroadcastPrices",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "complexType": {
          "original": "PricesDescription",
          "resolved": "{ prices: Prices; priceFromLabel?: string; }",
          "references": {
            "PricesDescription": {
              "location": "import",
              "path": "./types"
            }
          }
        }
      }, {
        "method": "ccBtnBuyOnlineAvailability",
        "name": "ccBtnBuyOnlineAvailability",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "complexType": {
          "original": "ButtonBuyOnlineAvailability",
          "resolved": "{ [x: string]: boolean; }",
          "references": {
            "ButtonBuyOnlineAvailability": {
              "location": "import",
              "path": "./types"
            }
          }
        }
      }, {
        "method": "ccGetOptionsSucceeded",
        "name": "ccGetOptionsSucceeded",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "complexType": {
          "original": "CommerceConnectorOptions",
          "resolved": "{ enableChannelSight: boolean; enableChannelSightOnCart: boolean; countryLanguageCode: string; feedUrl: string; circularFeedUrl?: string; enablePrice?: boolean; priceTemplate?: string; searchOnOpen?: boolean; useZipCode?: boolean; enableEmptyZipCode?: boolean; detectZipCode?: boolean; zipCodeLength?: number; zipCodePattern?: RegExp; enableNewDesign?: boolean; isApcSite?: boolean; apcCtaUrl?: string; agentChatCscId?: string; }",
          "references": {
            "CommerceConnectorOptions": {
              "location": "import",
              "path": "./types"
            }
          }
        }
      }];
  }
  static get watchers() {
    return [{
        "propName": "products",
        "methodName": "onProductsChange"
      }, {
        "propName": "productIdToShow",
        "methodName": "onProductToShowIdChange"
      }, {
        "propName": "productsDistributorsInfo",
        "methodName": "distributorsChangeHandler"
      }];
  }
  static get listeners() {
    return [{
        "name": "didChipSelect",
        "method": "onItemSelect",
        "target": undefined,
        "capture": false,
        "passive": false
      }, {
        "name": "backdrop",
        "method": "dialogCloseHandler",
        "target": undefined,
        "capture": false,
        "passive": false
      }, {
        "name": "didCloseDialog",
        "method": "dialogCloseHandler",
        "target": undefined,
        "capture": false,
        "passive": false
      }, {
        "name": "favoriteRetailIconClick",
        "method": "favoriteRetailIconClickHandler",
        "target": undefined,
        "capture": false,
        "passive": false
      }, {
        "name": "buyOnlineSearchClick",
        "method": "buyOnlineSearchClickHandler",
        "target": undefined,
        "capture": false,
        "passive": false
      }, {
        "name": "buyOnlineSearchInputChanged",
        "method": "buyOnlineSearchInputChangedHandler",
        "target": undefined,
        "capture": false,
        "passive": false
      }, {
        "name": "circularProductStockAvailabilityEvent",
        "method": "onCircularProductStockAvailabilityEvent",
        "target": "document",
        "capture": false,
        "passive": false
      }];
  }
}
